// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// Store
import { Store } from '@ngrx/store';
// Translate
import { TranslateService } from '@ngx-translate/core';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Auth
import { AuthNoticeService, AuthService, Login } from '../../../../core/auth';
import { getEmailErrorMessage, getPasswordErrorMessage } from '../../../../core/auth/_utils/auth-validation.util';
import { AppState } from '../../../../core/reducers';
import { environment } from '../../../../../environments/environment';
import { getBrandingLogoUrl } from '../../../../utils/general-utils';
import CryptoES from 'crypto-es';

@Component({
	selector: 'kt-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
	hostClient = location.host;
	// Public params
	loginForm: UntypedFormGroup;
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];
	isRemember = false;
	environment = environment;
	isSignup = environment.runtime.commercialized;
	enableOAuthSSO = environment.runtime.enableOAuthSSO;
	enableSAMLSSO = environment.runtime.enableSAMLSSO;
	code = "";
	private returnUrl: any;
	recaptcha: boolean;

	private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
	gCaptchaResponse: any;
	isCaptchaSelected: boolean = true
	siteKey = environment.runtime.siteKey
	authflag: boolean = environment.runtime.auth2fa;
	logoUrl: string = '';
	authSuccess: boolean = false;
	emailErrorMessage: string = '';
	passwordErrorMessage: string = '';
	imagePaths = {
		uptimeLogoOriginal: "assets/media/logos/logo-loader-colour.svg",
		signinLight: "assets/media/logos/ms-symbollockup_signin_light.svg",
		microsoftLogo: "assets/media/logos/microsoft-logo.png"
	}

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 */
	constructor(
		private readonly router: Router,
		private readonly auth: AuthService,
		private readonly authNoticeService: AuthNoticeService,
		private readonly translate: TranslateService,
		private readonly store: Store<AppState>,
		private readonly fb: UntypedFormBuilder,
		private readonly cdr: ChangeDetectorRef,
		public route: ActivatedRoute
	) {
		this.unsubscribe = new Subject();
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/web/dashboard';
		// this.router.navigateByUrl(this.returnUrl);
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		sessionStorage.removeItem('femail');
		if (this.authSuccess)
			this.authNoticeService.setNotice(null);
		this.recaptcha = environment.runtime.recaptcha;
		if (this.route.snapshot.queryParams['code']) {
			this.code = this.route.snapshot.queryParams['code']
			this.submitOAuthSSO(1)
		} else if (this.route.snapshot.queryParams['SAMLResponse']) {
			this.code = this.route.snapshot.queryParams['SAMLResponse']
			this.submitOAuthSSO(2)
		} else if (this.route.snapshot.queryParams['SoAkIaFDah']) {
			this.code = this.route.snapshot.queryParams['SoAkIaFDah'];
			this.submitOAuthSSO(3);
		}

		this.initLoginForm();
		this.loginForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.updateErrors();
		});
		this.updateErrors();
		//this.router.navigateByUrl('/web/dashboard');

		// redirect back to the returnUrl before login
		// this.route.queryParams.subscribe(params => {
		// 	this.returnUrl = params['returnUrl'] || '/';
		// });
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/web/dashboard';
		let rawLogo: string | undefined;
		try {
			rawLogo = JSON.parse(localStorage.getItem('layoutConfig') || '{}')?.['logo'];
		} catch (e) {
			rawLogo = undefined;
		}
		this.logoUrl = getBrandingLogoUrl(rawLogo);
	}


	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.unsubscribe.next(undefined);
		this.unsubscribe.complete();
		this.loading = false;
	}

	// SSO Type: 1
	redirectUser() {
		location.href = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" + environment.runtime.ssoClientId + "&response_type=code&redirect_uri=" + environment.runtime.redirectURISSO + "&response_mode=query&scope=openid profile offline_access email&state=12345";
	}

	// SSO Type: 2
	redirectUserADFS() {
		// No implementation as of now.
		location.href = '';
	}

	// SSO Type: 3
	redirectUserSAML() {
		const returnUrl = this.route.snapshot.queryParams['returnUrl'];
		if (returnUrl) {
			sessionStorage.setItem('samlReturnUrl', returnUrl);
		}
		this.auth.redirectToSAML();
	}


	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {
		this.loginForm = this.fb.group({
			email: ['', Validators.compose([
				Validators.required,
				Validators.email,
				Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
			])
			],
			password: ['', Validators.compose([
				Validators.required,
			])
			]
		});
	}

	onRememBerMe(e) {
		this.isRemember = e.checked;
	}

	resolved(e) {
		this.gCaptchaResponse = e
		this.isCaptchaSelected = e ? false : true;
	}

	async submitOAuthSSO(idptype) {
		this.loading = true;
		this.auth
			.IDPlogin({ code: this.code, idptype: idptype, redirect_uri: environment.runtime.redirectURISSO })
			.pipe(
				tap((res: any) => {
					if (res.status == 500) {
						this.authNoticeService.setNotice(this.translate.instant(res.body.message ? res.body.message : 'AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
					} else if (res.headers.get('x-auth-token')) {
						if (res.body?.samlSessionIndex) {
							localStorage.setItem('samlSessionIndex', res.body.samlSessionIndex);
						}
						this.store.dispatch(new Login({ authToken: res.headers.get('x-auth-token') }));
					} else {
						this.authNoticeService.setNotice(this.translate.instant(res.body.message ? res.body.message : 'AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
					}
				}),
				takeUntil(this.unsubscribe),
				finalize(() => {

					this.loading = false;
					this.code = "";
					this.cdr.detectChanges();
				})
			)
			.subscribe();
	}

	/**
	 * Form Submit
	 */
	async submit() {
		const controls = this.loginForm.controls;
		/** check form */
		if (this.loginForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			this.updateErrors();
			return;
		}

		this.loading = true;

		const authData = {
			email: controls['email'].value,
			password: controls['password'].value,
			'g-recaptcha-response': this.gCaptchaResponse
		};
		const encryptedPassword = CryptoES.AES.encrypt(authData.password, environment.runtime.authTokenKey).toString();
		this.auth
			.login(authData.email.toLowerCase(), encryptedPassword, this.isRemember, authData['g-recaptcha-response'])
			.pipe(
				tap((res: any) => {
					if (res.status == 200 && res.body.status == 200 && (this.authflag)) {
						sessionStorage.setItem('femail', authData.email);
						this.authSuccess = true;
						this.router.navigateByUrl('/web/verify-otp');
					}
					else if (res.headers.get('x-auth-token') && !this.authflag) {
						const refreshToken = res.headers.get('refresh-token');
						this.store.dispatch(new Login({ authToken: res.headers.get('x-auth-token'), refreshToken: refreshToken }));
					}
					else {
						if (environment.runtime.recaptcha)
							grecaptcha.reset()
						this.isCaptchaSelected = true;
						this.authNoticeService.setNotice(this.translate.instant(res.body.message ? res.body.message : 'AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
					}
				}),
				takeUntil(this.unsubscribe),
				finalize(() => {
					this.loading = false;
					this.cdr.detectChanges();
				})
			)
			.subscribe();
	}

	updateErrors(): void {
		this.emailErrorMessage = getEmailErrorMessage(this.loginForm.get('email'), this.translate);
		this.passwordErrorMessage = getPasswordErrorMessage(this.loginForm.get('password'), this.translate);
	}
}
